import { PractitionerResourceOption } from '@/components/PractitionerResourceOption';
import { alert } from '@/services/alert';
import { fetchBot } from '@/services/services';
import { Button, Flex, Stack } from '@mantine/core';
import { createReference } from '@medplum/core';
import { Practitioner, Reference } from '@medplum/fhirtypes';
import { ResourceInput, useMedplum } from '@medplum/react';
import { useState, useCallback } from 'react';
import { useCareTeamManagement } from '../../hooks/useCareTeamManagement';
import { useTimelineContext } from '../../hooks/useTimelineContext';
import { InfoCard } from '../shared/InfoCard';
import { UnifiedCareTeamList } from './UnifiedCareTeamList';

function AddCareTeamMemberForm({
  onAdd,
  onClose,
}: {
  onAdd: (item: { practitioner: Practitioner }) => void;
  onClose: () => void;
}) {
  const [selectedPractitioner, setSelectedPractitioner] = useState<Practitioner | null>(null);

  const handleAdd = async () => {
    if (!selectedPractitioner) return;
    await onAdd({ practitioner: selectedPractitioner });
    setSelectedPractitioner(null);
    onClose();
  };

  return (
    <Stack gap={8} mb={8}>
      <div onMouseDown={(e) => e.stopPropagation()}>
        <ResourceInput
          name="provider"
          resourceType="Practitioner"
          onChange={(resource) => setSelectedPractitioner((resource as Practitioner) || null)}
          placeholder="Search practitioner"
          defaultValue={undefined}
          itemComponent={(props: { resource: Practitioner }) => (
            <PractitionerResourceOption resource={props.resource} />
          )}
        />
      </div>

      <Flex justify="flex-end" gap={8}>
        <Button variant="subtle" color="blue" radius="sm" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button color="blue" radius="lg" size="sm" onClick={handleAdd} disabled={!selectedPractitioner}>
          Save
        </Button>
      </Flex>
    </Stack>
  );
}

export function CareTeamCard() {
  const medplum = useMedplum();
  const { patientContext } = useTimelineContext();
  const patient = patientContext?.patient;
  const { providers, addCareTeamProvider, removeCareTeamProvider, isSaving } = useCareTeamManagement();

  const handleSetConversationLead = useCallback(
    async (providerRef: Reference<Practitioner> | null) => {
      if (!patient) return;
      try {
        await fetchBot(medplum, 'set-conversation-lead', {
          patientRef: createReference(patient),
          providerRef: providerRef,
        });
      } catch (e) {
        const action = providerRef ? 'assign' : 'unassign';
        alert.error(`Couldn't ${action} Conversation Lead: ${e instanceof Error ? e.message : 'Unknown error'}`);
      }
    },
    [medplum, patient]
  );

  const handleUnsetConversationLead = useCallback(async () => {
    await handleSetConversationLead(null);
  }, [handleSetConversationLead]);

  return (
    <InfoCard
      title="Care team members"
      addButtonAriaLabel="Add care team member"
      defaultOpen={false}
      renderList={() => (
        <UnifiedCareTeamList
          items={providers}
          onSetConversationLead={handleSetConversationLead}
          onUnsetConversationLead={handleUnsetConversationLead}
          onRemoveMember={removeCareTeamProvider}
          isSaving={isSaving}
        />
      )}
      renderAddForm={(onClose) => <AddCareTeamMemberForm onAdd={addCareTeamProvider} onClose={onClose} />}
    />
  );
}
